/* istanbul ignore file */
import React from 'react';

import GridBagRuler from './GridBagRuler';
import GridBagItem from './GridBagItem';
import { supportedBreakpoints as breakpointNames } from '../../shared/breakpoints';
import { columnDefinitionAtBreakpoints } from './GridBagHelpers';

import styles from './GridBag.module.scss';

const columnwiseSum = (leftArray: number[], rightArray: number[]) => {
  return leftArray.map((item, index: number) => item + rightArray[index]);
};

const wrapColumns = (columnArray: number[], definitions: number[]) => {
  const wraps = breakpointNames.map(() => 0);

  const wrappedArray = columnArray.map((item, index: number) => {
    if (item > 12) {
      wraps[index]++;
      return definitions[index];
    }
    return item;
  });

  return [wrappedArray, wraps];
};

type rowItem = { rowPositions: number[]; columnPositions: number[] };

type GridBagChild =
  | React.ReactElement
  | boolean
  | null
  | undefined
  | GridBagChild[];

type GridBagProps = {
  /** Show helper lines */
  showGridLines?: boolean;
  /** Remove spaces in between columns */
  noGutter?: boolean;
  /** Remove horizontal space around the children */
  noHorizontalGutter?: boolean;
  /** Remove vertical space around the children */
  noVerticalGutter?: boolean;
  children: GridBagChild;
} & Omit<JSX.IntrinsicElements['div'], 'children'>;

const GridBag: React.FunctionComponent<GridBagProps> & {
  Item: typeof GridBagItem;
  Ruler: typeof GridBagRuler;
} = ({ children, noGutter, noHorizontalGutter, noVerticalGutter }) => {
  if (noGutter) {
    noHorizontalGutter = true;
    noVerticalGutter = true;
  }

  const childArray = React.Children.toArray(children) as React.ReactElement[];
  const emptyColumns = breakpointNames.map(() => 0);

  // settingup item's row and column positions
  const itemSetup = childArray.reduce(
    (accumulator: rowItem[] | [], child: React.ReactElement, index: number) => {
      const definitions = columnDefinitionAtBreakpoints(child?.props);
      const previousSetup =
        index > 0
          ? accumulator[index - 1]
          : {
              cumulativeColumnCounts: [...emptyColumns],
              rowPositions: [...emptyColumns],
              columnPositions: [...emptyColumns],
            };
      const [columnPositions, columnWraps] = wrapColumns(
        columnwiseSum(previousSetup.columnPositions, definitions),
        definitions
      );

      const rowPositions = columnwiseSum(
        previousSetup.rowPositions,
        columnWraps
      );
      return [
        ...accumulator,
        {
          rowPositions,
          columnPositions,
        },
      ];
    },
    [] as rowItem[]
  );

  const lastRows = itemSetup.length
    ? itemSetup[itemSetup.length - 1].rowPositions
    : [...emptyColumns];

  return (
    <div className={styles.gridBag}>
      <div className={styles.gridBag_contents}>
        {/* children as GridBag.Item */}
        {childArray.map((child: React.ReactElement, index: number) => {
          const setup = itemSetup[index];

          const onLastColumn = setup.columnPositions.map(
            (position: number) => position === 12
          );

          const onLastRow = setup.rowPositions.map(
            (position: number, index: number) => position === lastRows[index]
          );

          return (
            <GridBagItem
              key={String(index)}
              {...child?.props}
              noHorizontalGutter={noHorizontalGutter}
              noVerticalGutter={noVerticalGutter}
              onLastColumn={onLastColumn}
              onLastRow={onLastRow}
            />
          );
        })}
      </div>
    </div>
  );
};

GridBag.Item = () => null;

GridBag.Ruler = GridBagRuler;

export default GridBag;
